import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../utils/apiConfig';

// Define languages here to avoid circular dependency with i18n
const SUPPORTED_LANGUAGES = ['en', 'ru', 'ko', 'zh'];
const DEFAULT_LANGUAGE = 'en';

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [language, setLanguageState] = useState(DEFAULT_LANGUAGE);
  const [isInitialized, setIsInitialized] = useState(false);

  // Extract language from URL path
  const getLanguageFromPath = useCallback((pathname) => {
    const pathParts = pathname.split('/').filter(Boolean);
    if (pathParts.length > 0 && SUPPORTED_LANGUAGES.includes(pathParts[0])) {
      return pathParts[0];
    }
    return null;
  }, []);

  // Get path without language prefix
  const getPathWithoutLang = useCallback((pathname) => {
    const pathParts = pathname.split('/').filter(Boolean);
    if (pathParts.length > 0 && SUPPORTED_LANGUAGES.includes(pathParts[0])) {
      const remainingPath = pathParts.slice(1).join('/');
      return remainingPath ? '/' + remainingPath : '/trades';
    }
    return pathname || '/trades';
  }, []);

  // Build path with language prefix
  const buildPathWithLang = useCallback((basePath, lang) => {
    const cleanPath = basePath.startsWith('/') ? basePath : '/' + basePath;

    // For English (default), no prefix needed
    if (lang === DEFAULT_LANGUAGE) {
      return cleanPath;
    }

    return `/${lang}${cleanPath}`;
  }, []);

  // Initialize language on mount
  useEffect(() => {
    const initLanguage = async () => {
      // 1. Check URL for language
      const urlLang = getLanguageFromPath(location.pathname);
      if (urlLang) {
        setLanguageState(urlLang);
        i18n.changeLanguage(urlLang);
        setIsInitialized(true);
        return;
      }

      // 2. Check localStorage
      const savedLang = localStorage.getItem('crypnal_language');
      if (savedLang && SUPPORTED_LANGUAGES.includes(savedLang)) {
        setLanguageState(savedLang);
        i18n.changeLanguage(savedLang);
        setIsInitialized(true);
        return;
      }

      // 3. Try to detect via API (GeoIP)
      try {
        const response = await fetch(`${API_BASE_URL}/api/detect-language`);
        const data = await response.json();
        if (data.success && data.recommended && SUPPORTED_LANGUAGES.includes(data.recommended)) {
          const detectedLang = data.recommended;
          setLanguageState(detectedLang);
          i18n.changeLanguage(detectedLang);
          localStorage.setItem('crypnal_language', detectedLang);

          // Redirect to language-prefixed URL if not English
          if (detectedLang !== DEFAULT_LANGUAGE) {
            const newPath = buildPathWithLang(location.pathname, detectedLang);
            navigate(newPath, { replace: true });
          }

          setIsInitialized(true);
          return;
        }
      } catch (error) {
        console.error('Language detection failed:', error);
      }

      // 4. Fallback to default
      setLanguageState(DEFAULT_LANGUAGE);
      i18n.changeLanguage(DEFAULT_LANGUAGE);
      setIsInitialized(true);
    };

    initLanguage();
  }, []); // Run only once on mount

  // Sync language when URL changes
  useEffect(() => {
    if (!isInitialized) return;

    const urlLang = getLanguageFromPath(location.pathname);
    if (urlLang && urlLang !== language) {
      setLanguageState(urlLang);
      i18n.changeLanguage(urlLang);
      localStorage.setItem('crypnal_language', urlLang);
    }
  }, [location.pathname, isInitialized]);

  // Update HTML lang attribute for SEO and accessibility
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Change language and update URL
  const setLanguage = useCallback((newLang) => {
    if (!SUPPORTED_LANGUAGES.includes(newLang)) {
      console.error(`Unsupported language: ${newLang}`);
      return;
    }

    if (newLang === language) return;

    // Update state
    setLanguageState(newLang);
    i18n.changeLanguage(newLang);
    localStorage.setItem('crypnal_language', newLang);

    // Update URL without triggering React Router navigation
    // This prevents component remounting when switching languages
    const basePath = getPathWithoutLang(location.pathname);
    const newPath = buildPathWithLang(basePath, newLang);

    console.log('[i18n] Language switch:', {
      from: language,
      to: newLang,
      currentPath: location.pathname,
      basePath,
      newPath
    });

    if (newPath !== location.pathname) {
      // Use replaceState to update URL without remounting components
      window.history.replaceState(null, '', newPath);
    }
  }, [language, location.pathname, i18n, getPathWithoutLang, buildPathWithLang]);

  // Build localized link href
  const localizedPath = useCallback((path) => {
    return buildPathWithLang(path, language);
  }, [language, buildPathWithLang]);

  const value = {
    language,
    setLanguage,
    localizedPath,
    isInitialized,
    supportedLanguages: SUPPORTED_LANGUAGES,
    defaultLanguage: DEFAULT_LANGUAGE
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE };
